<script setup>
import { ref, watch, nextTick } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  // Pre-filled display text (e.g. the currently selected text in the editor).
  initialText: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['save', 'close']);

const text = ref('');
const url = ref('');
const linkInput = ref(null);

// Only (re)initialise the fields on a real closed -> open transition, so the
// values the user types are never wiped while the modal stays open.
watch(
  () => props.show,
  async (isShown, wasShown) => {
    if (isShown && !wasShown) {
      text.value = props.initialText || '';
      url.value = '';
      await nextTick();
      linkInput.value?.focus();
    }
  },
  { immediate: true }
);

const onClose = () => emit('close');

const onSave = () => {
  const link = url.value.trim();
  if (!link) return;
  emit('save', { text: text.value.trim(), url: link });
  onClose();
};

// Stop keystrokes from bubbling to the editor / global keyboard shortcuts
// (which otherwise swallow characters), and submit on Enter.
const onFieldKeydown = e => {
  e.stopPropagation();
  if (e.key === 'Enter') {
    e.preventDefault();
    onSave();
  }
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col add-link">
      <!-- Header -->
      <div class="px-6 pt-6 pb-4 border-b border-n-weak">
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ $t('CONVERSATION.ADD_LINK.TITLE') }}
        </h3>
      </div>

      <!-- Body -->
      <div class="flex flex-col gap-4 px-6 py-6">
        <label class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CONVERSATION.ADD_LINK.TEXT_LABEL') }}
          </span>
          <input
            v-model="text"
            type="text"
            class="add-link__input"
            :placeholder="$t('CONVERSATION.ADD_LINK.TEXT_PLACEHOLDER')"
            @keydown="onFieldKeydown"
          />
        </label>
        <label class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CONVERSATION.ADD_LINK.LINK_LABEL') }}
          </span>
          <input
            ref="linkInput"
            v-model="url"
            type="url"
            class="add-link__input"
            placeholder="https://"
            @keydown="onFieldKeydown"
          />
        </label>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-start gap-4 px-6 py-4 border-t border-n-weak"
      >
        <NextButton
          :label="$t('CONVERSATION.ADD_LINK.SAVE')"
          size="sm"
          color="blue"
          :disabled="!url.trim()"
          @click="onSave"
        />
        <NextButton
          :label="$t('CONVERSATION.ADD_LINK.CANCEL')"
          size="sm"
          variant="link"
          color="blue"
          @click="onClose"
        />
      </div>
    </div>
  </woot-modal>
</template>

<style scoped lang="scss">
.add-link__input {
  @apply w-full h-10 px-3 text-sm rounded-lg text-n-slate-12 bg-n-alpha-black2
    outline outline-1 outline-n-weak transition-colors;

  &::placeholder {
    @apply text-n-slate-10;
  }

  &:hover {
    @apply outline-n-slate-6;
  }

  &:focus {
    @apply outline-2 outline-n-brand;
  }
}
</style>
